import React, { useContext, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { ThemeContext } from 'styled-components';
import { Charts, Text } from '@perch-labs/shrike';
import { copyFor, routes } from 'config';
import { energySourceLabelMap } from 'domain/usage';
import { roundWithCommas } from 'utils/format';
import { Base, ButtonChip } from 'styles';
import { buildDonutData } from './energy-source-breakdown.utils';
import {
  AdditionalInfo,
  Container,
  DonutWrapper,
  EquivalencyStat,
  LearnMoreLink,
} from './energy-source-breakdown.style';

const getCopy = copyFor('modules.energySourceBreakdown');

const energySourceUnitsMap = {
  cubic_feet: 'cubic feet',
  gallons: 'gallons',
  grams: 'grams',
  pounds: 'pounds',
};

const energySourceUnitsLabelMap = {
  coal: 'coal',
  gas: 'gas',
  nuclear: 'uranium',
  oil: 'oil',
};

// TODO: move new size percentage to shrike
const chartOptions = {
  grid: { padding: { bottom: 25, top: 25 } },
  plotOptions: {
    pie: {
      donut: {
        size: '80%',
      },
      expandOnClick: false,
    },
  },
};
interface FuelTypeEquivalencyProperty {
  equivalency: number;
  units: string;
}

interface Props {
  className?: string;
  energyBreakdown: {
    coal: number;
    gas: number;
    nuclear: number;
    oil: number;
  };
  fuelTypeEquivalency: {
    coal: FuelTypeEquivalencyProperty;
    gas: FuelTypeEquivalencyProperty;
    nuclear: FuelTypeEquivalencyProperty;
    oil: FuelTypeEquivalencyProperty;
  };
  withLink?: boolean;
}

type ActiveEnergySource = 'coal' | 'gas' | 'nuclear' | 'oil';

export const EnergySourceBreakdown = ({
  className = '',
  energyBreakdown,
  fuelTypeEquivalency,
  withLink = true,
}: Props) => {
  const theme = useContext(ThemeContext);
  const { planId } = useParams<{ planId?: string }>();
  const [activeEnergySource, setActiveEnergySource] =
    useState<ActiveEnergySource>('coal');
  const history = useHistory();
  const from = history.location.pathname;
  if (!energyBreakdown) return null;
  const { energyDetails } = routes({ source: activeEnergySource, planId });
  const sourceEquivalence = fuelTypeEquivalency[activeEnergySource];
  const { coal, gas, nuclear, oil } = energyBreakdown;
  const other = 1 - (coal + gas + nuclear + oil);
  const navigation = { coal, gas, nuclear, oil };
  const donutBreakdown = { ...navigation, other };
  const series = buildDonutData({
    activeEnergySource,
    donutBreakdown,
    energySourceLabelMap,
    theme,
  });

  return (
    <div className={className}>
      <Base>
        {(Object.keys(navigation) as ActiveEnergySource[]).map(key => (
          <ButtonChip
            key={key}
            active={key === activeEnergySource}
            onClick={() => setActiveEnergySource(key)}
            aria-label={getCopy('aria.buttons.tab', {
              source: energySourceLabelMap[key],
            })}
          >
            {getCopy('tabButtomLabel', { label: energySourceLabelMap[key] })}
          </ButtonChip>
        ))}
      </Base>
      <Container>
        <AdditionalInfo>
          <Text textStyle="heading500" tag="h2" margin={{ bottom: '8px' }}>
            About {energySourceLabelMap[activeEnergySource]}
          </Text>
          <Text textStyle="paragraph400" margin={{ bottom: '16px' }}>
            {getCopy(activeEnergySource)}
          </Text>
          {withLink && (
            <LearnMoreLink
              aria-label={getCopy('aria.links.learnMore', {
                activeEnergySource,
              })}
              to={{
                pathname: energyDetails,
                state: { from },
              }}
            >
              {getCopy('links.learnMore')}
            </LearnMoreLink>
          )}
        </AdditionalInfo>
        <DonutWrapper>
          <EquivalencyStat
            aria-labelledby={getCopy('aria.links.learnMore', {
              activeEnergySource,
            })}
          >
            <Text textStyle="heading900">
              {roundWithCommas(sourceEquivalence.equivalency)}
            </Text>
            <Text textStyle="paragraph400">
              {getCopy('impact.line1', {
                units: energySourceUnitsMap[sourceEquivalence.units],
              })}
              <br />
              {getCopy('impact.line2', {
                source: energySourceUnitsLabelMap[activeEnergySource],
              })}
            </Text>
          </EquivalencyStat>
          <Charts.Donut
            aria-hidden={true}
            id="usage-breakdown"
            series={series}
            showLegend={false}
            options={chartOptions}
          />
        </DonutWrapper>
      </Container>
    </div>
  );
};
